import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const dropdownRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const lastNotificationCountRef = useRef(0);

  // Request push notification permission on mount
  useEffect(() => {
    if (!user) return;
    
    // Check if browser supports notifications
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
      
      // Auto-request permission if not decided yet
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setPushPermission(permission);
          console.log('🔔 Push notification permission:', permission);
        });
      }
    }
  }, [user]);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      try {
        const count = await notificationAPI.getUnreadCount();
        
        // Check if new notification arrived
        if (count > lastNotificationCountRef.current && count > 0) {
          // Fetch latest notification to show as push
          const notifs = await notificationAPI.getAll(1, true); // Get 1 unread notification
          if (notifs.length > 0) {
            showBrowserNotification(notifs[0]);
          }
        }
        
        lastNotificationCountRef.current = count;
        setUnreadCount(count);
      } catch (error) {
        // Silently ignore network errors when user is not authenticated
        if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
          // User likely not authenticated or connection closed - ignore
          return;
        }
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();

    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [user]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
    }
  }, [isOpen, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Show browser push notification
  const showBrowserNotification = (notification) => {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      // Create notification with appropriate icon and message
      const notif = new Notification(notification.title || 'New Notification', {
        body: notification.message,
        icon: '/logo192.png', // Your app icon
        badge: '/logo192.png',
        tag: notification.id, // Prevent duplicate notifications
        requireInteraction: notification.type === 'team_invitation', // Keep invitation notifications visible
        data: {
          notificationId: notification.id,
          type: notification.type,
          team_id: notification.team_id,
          invitation_id: notification.invitation_id
        }
      });

      // Handle notification click
      notif.onclick = async () => {
        window.focus(); // Focus the browser window
        
        // Mark as read
        try {
          await notificationAPI.markAsRead(notification.id);
          setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }

        // Navigate based on type
        if (notification.type === 'team_invitation') {
          navigate('/team-settings');
        } else if (notification.type === 'follow_up_overdue' || notification.type === 'follow_up_due') {
          if (notification.lead_id) {
            sessionStorage.setItem('openLeadId', notification.lead_id);
          }
          navigate('/crm');
        } else if (notification.type === 'new_visitor') {
          navigate('/visitors');
        } else {
          navigate(`/bio/${notification.from_username}`);
        }

        notif.close();
      };

      console.log('🔔 Browser push notification shown:', notification.title);
    } catch (error) {
      console.error('Error showing browser notification:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const notifs = await notificationAPI.getAll(20, false); // Get last 20 notifications
      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      if (!notification.is_read) {
        await notificationAPI.markAsRead(notification.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Update local notification state
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      }

      // Close dropdown
      setIsOpen(false);

      // Navigate based on notification type
      if (notification.type === 'follow_up_overdue' || notification.type === 'follow_up_due') {
        console.log('🔔 Follow-up notification clicked:', notification);
        // For CRM follow-up notifications, navigate to CRM page
        // Store the lead ID in sessionStorage so CRM page can open the dialog
        if (notification.lead_id) {
          console.log('📝 Storing lead ID in sessionStorage:', notification.lead_id);
          sessionStorage.setItem('openLeadId', notification.lead_id);
        } else {
          console.warn('⚠️ No lead_id in notification');
        }
        console.log('🚀 Navigating to CRM page');
        navigate('/crm');
      } else if (notification.type === 'demo_request') {
        console.log('🔔 Demo request notification clicked:', notification);
        // Navigate to CRM page for demo requests
        navigate('/crm');
      } else if (notification.type === 'team_invitation') {
        console.log('🔔 Team invitation notification clicked:', notification);
        // Navigate to team settings to view and accept invitation
        navigate('/team-settings');
      } else if (notification.type === 'new_visitor') {
        console.log('🔔 New visitor notification clicked:', notification);
        // Navigate to visitors page for visitor notifications
        navigate('/visitors');
      } else {
        // For other notifications (e.g., new connections), navigate to bio page
        navigate(`/bio/${notification.from_username}`);
      }
    } catch (error) {
      console.error('Error handling notification:', error);
      toast({
        title: "Error",
        description: "Failed to process notification. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      
      toast({
        title: "All notifications marked as read",
        description: "Your notification inbox is now clear."
      });
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark notifications as read.",
        variant: "destructive"
      });
    }
  };

  const formatTimeAgo = (dateString) => {
    // Parse the UTC timestamp correctly
    // Backend sends ISO 8601 format with timezone info
    const date = new Date(dateString);
    const now = new Date();
    
    // Calculate difference in seconds
    const seconds = Math.floor((now - date) / 1000);

    // Handle negative values (future dates due to timezone issues)
    if (seconds < 0) {
      return 'Just now';
    }

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    
    // For older dates, use locale-specific formatting
    return date.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button - Updated for Black Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 text-white transition-colors shadow-md border border-gray-700 flex-shrink-0"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center shadow-md border-2 border-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-md bg-white rounded-lg shadow-2xl border-2 border-gray-200 z-[70] max-h-[70vh] sm:max-h-[500px] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-teal-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
              {notifications.length > 0 && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-amber-600 hover:text-amber-800 font-semibold"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Bell className="w-12 h-12 text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm text-center">No notifications yet</p>
                <p className="text-gray-400 text-xs text-center mt-1">
                  You'll be notified when someone adds your card
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      notification.is_read
                        ? 'bg-white hover:bg-gray-50'
                        : 'bg-blue-50 hover:bg-blue-100'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread Indicator */}
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        {/* Show title for demo_request type */}
                        {notification.title && (
                          <p className={`text-sm font-semibold ${notification.is_read ? 'text-gray-800' : 'text-gray-900'}`}>
                            {notification.title}
                          </p>
                        )}
                        <p className={`text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-700'} ${notification.title ? 'mt-0.5' : ''}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>

                      {/* Arrow Icon */}
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - Only show if there are notifications */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Could navigate to a full notifications page if you create one
                }}
                className="w-full text-center text-xs text-amber-600 hover:text-amber-800 font-semibold py-1"
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
